import { useEffect, useState } from "react";
import {
  Button,
  Callout,
  Drawer,
  DrawerSection,
  FailedCallout,
  ReasonField,
  Tag,
  useReason,
  useToast,
  errorText,
  useCommandMutation,
} from "@amber/ui";
import type { ProfileDetail, RefreshProposal } from "../../contracts/index";
import { useProfileInvalidation } from "../invalidation";
import { profileCommands } from "../queries";

type Edition = RefreshProposal["editions"][number];
type Difference = RefreshProposal["differences"][number];
type Decisions = Record<string, "accept" | "reject">;

function editionState(e: Edition) {
  if (e.state === "read") return "read";
  if (e.state === "missing") return "no article";
  return <span className="text-attention">couldn&apos;t be read: {e.detail}</span>;
}

function kindLabel(kind: Difference["kind"]): string {
  if (kind === "added") return "New";
  if (kind === "removed") return "No longer in the source";
  if (kind === "conflict") return "Conflicting";
  return "Changed";
}

function applyBlocked(proposal: RefreshProposal | null, open: number): string | null {
  if (!proposal) return "Reading the linked editions…";
  if (open > 0) return `${String(open)} difference${open === 1 ? "" : "s"} still need a decision`;
  return null;
}

function DifferenceItem({
  d,
  decisions,
  setDecisions,
}: {
  d: Difference;
  decisions: Decisions;
  setDecisions: (next: Decisions) => void;
}) {
  return (
    <li className="rounded-lg border border-divider p-2">
      <div className="flex items-baseline gap-2">
        <b className="font-strong">{d.label}</b>
        <Tag tone={d.kind === "removed" || d.kind === "conflict" ? "waiting" : "neutral"}>{kindLabel(d.kind)}</Tag>
      </div>
      <dl className="m-0 mt-1 grid grid-cols-[70px_1fr] gap-x-2 text-md">
        <dt className="text-muted">Now</dt>
        <dd className="m-0">{d.now ?? "—"}</dd>
        <dt className="text-muted">Proposed</dt>
        <dd className="m-0">{d.proposed ?? "Remove"}</dd>
        <dt className="text-muted">Evidence</dt>
        <dd className="m-0 text-muted">{d.evidence ?? "—"}</dd>
      </dl>
      <span className="mt-1.5 flex gap-1.5" role="radiogroup" aria-label={`Decision for ${d.label}`}>
        {(["accept", "reject"] as const).map((v) => (
          <Button
            key={v}
            size="xs"
            variant={decisions[d.key] === v ? "primary" : "default"}
            aria-pressed={decisions[d.key] === v}
            onClick={() => {
              setDecisions({ ...decisions, [d.key]: v });
            }}
          >
            {v === "accept" ? "Accept" : "Reject"}
          </Button>
        ))}
      </span>
    </li>
  );
}

function ProposalSections({
  proposal,
  decisions,
  setDecisions,
}: {
  proposal: RefreshProposal;
  decisions: Decisions;
  setDecisions: (next: Decisions) => void;
}) {
  return (
    <>
      <DrawerSection title="Editions read">
        <ul className="m-0 list-none p-0 text-md">
          {proposal.editions.map((e) => (
            <li key={e.edition}>
              {e.edition}.wikipedia · {editionState(e)}
            </li>
          ))}
        </ul>
      </DrawerSection>
      <DrawerSection title={`Proposed differences (${String(proposal.differences.length)})`}>
        {proposal.differences.length === 0 ? <p className="m-0 text-md text-muted">Nothing to change.</p> : null}
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {proposal.differences.map((d) => (
            <DifferenceItem key={d.key} d={d} decisions={decisions} setDecisions={setDecisions} />
          ))}
        </ul>
      </DrawerSection>
      {proposal.protected.length > 0 ? (
        <DrawerSection title="Protected manual values">
          <p className="m-0 text-md text-muted">Corrected by an Editor; never overwritten or removed by a refresh.</p>
          <ul className="m-0 mt-1.5 list-none p-0 text-md">
            {proposal.protected.map((p) => (
              <li key={p.key} className="border-t border-divider py-1.5">
                <b className="font-strong">{p.label}</b> · kept: {p.now ?? "—"}
                <span className="block text-muted">Wikipedia says: {p.source_value ?? "—"}</span>
              </li>
            ))}
          </ul>
        </DrawerSection>
      ) : null}
      {proposal.unchanged.length > 0 ? (
        <p className="m-0 text-md text-muted">Unchanged: {proposal.unchanged.join(", ")}.</p>
      ) : null}
    </>
  );
}

export function RefreshPanel({ profile, onClose }: { profile: ProfileDetail; onClose: () => void }) {
  const toast = useToast();
  const invalidation = useProfileInvalidation();
  const [proposal, setProposal] = useState<RefreshProposal | null>(profile.refresh);
  const [decisions, setDecisions] = useState<Decisions>({});
  const reason = useReason();
  const start = useCommandMutation({
    run: (csrf) => profileCommands.refresh(profile.id, csrf),
    ...invalidation,
    onSuccess: (p) => {
      setProposal(p);
      setDecisions({});
    },
  });
  const { mutate: startRefresh } = start;
  useEffect(() => {
    if (!profile.refresh) startRefresh();
  }, [profile.refresh, startRefresh]);
  const apply = useCommandMutation({
    run: (csrf, text: string) =>
      profileCommands.decideRefresh(
        profile.id,
        proposal?.id ?? "",
        { base_revision: profile.revision, reason: text, decisions },
        csrf,
      ),
    ...invalidation,
    onSuccess: (r) => {
      toast.show(`Refresh applied: ${String(r.accepted)} accepted, ${String(r.rejected)} rejected`);
      onClose();
    },
  });
  const open = proposal ? proposal.differences.filter((d) => !decisions[d.key]).length : 0;
  return (
    <Drawer
      title="Refresh from Wikipedia"
      subtitle={profile.name}
      onClose={onClose}
      footer={
        <span className="flex items-center justify-end gap-2">
          <span className="mr-auto text-md text-muted">Nothing changes until you confirm.</span>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            type="submit"
            form="refresh-form"
            disabledReason={applyBlocked(proposal, open)}
            aria-busy={apply.isPending || undefined}
          >
            Apply decisions
          </Button>
        </span>
      }
    >
      <form
        id="refresh-form"
        noValidate
        className="flex flex-col gap-4 text-ui"
        onSubmit={(e) => {
          e.preventDefault();
          if (reason.submit() && open === 0) apply.mutate(reason.trimmed);
        }}
      >
        {start.isPending ? <p className="m-0 text-md text-muted">Reading the linked editions…</p> : null}
        {start.error ? (
          <Callout
            tone="attention"
            role="alert"
            actions={
              <Button
                size="sm"
                onClick={() => {
                  start.mutate();
                }}
              >
                Retry
              </Button>
            }
          >
            {errorText(start.error)}
          </Callout>
        ) : null}
        {proposal ? (
          <>
            <ProposalSections proposal={proposal} decisions={decisions} setDecisions={setDecisions} />
            <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
          </>
        ) : null}
        <FailedCallout error={apply.error} className="" />
      </form>
    </Drawer>
  );
}
